import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getRangeValue } from 'component/Layout/Form/RangeSlider/__helper/getRangeValue';
import PriceSliderComponent from 'component/Layout/Form/RangeSlider/PriceSlider';
import { NumberTuple } from 'component/Layout/Form/RangeSlider/types';
import Segment from 'component/Search/Filter/Segment';

import classNames from 'classnames';

import { useBudgetAtom } from 'hooks/form/filter';
import { useBugdetMeta } from 'hooks/form/meta';
import { useChangedFilterListForSlider } from 'hooks/form/useChangedFilterList';
import { QaIdentifier } from 'hooks/useQa';

import { PriceRange } from 'proto/Search/PriceRange';

import { FilterKeys } from '../types';

import styles from './PriceSlider.module.scss';

const defaultBudget: PriceRange = {
    minPrice: 0,
    maxPrice: 0,
    rangeList: [],
};

const PriceSlider = () => {
    const { t } = useTranslation();
    const [budgetMeta] = useBugdetMeta();
    const [budget, setBudget] = useBudgetAtom();
    const { onSelect, onClear } = useChangedFilterListForSlider(
        FilterKeys.PriceRangeSlider,
        () => getRangeValue(budget)
    );
    const priceRange = useMemo(
        () => ({
            ...defaultBudget,
            ...budgetMeta,
            rangeList:
                budgetMeta?.rangeList.map((range) => ({
                    ...range,
                    min: Math.ceil(range.min) ?? 0,
                    max: Math.ceil(range.max) ?? 0,
                })) ?? defaultBudget.rangeList,
        }),
        [budgetMeta]
    );

    const initialIndexTuple: NumberTuple = useMemo(() => {
        const rangeList = priceRange.rangeList;
        const indexMin = rangeList.findIndex(
            (price) =>
                budget?.min &&
                budget.min >= price.min &&
                budget.min <= price.max
        );
        const indexMax =
            budget?.min === budget?.max
                ? indexMin
                : rangeList.findIndex(
                      (price) =>
                          budget?.max &&
                          budget.max <= price.max &&
                          budget.max >= price.min
                  );
        return [
            indexMin > -1 ? indexMin : 0,
            indexMax > -1
                ? indexMax
                : budget?.max &&
                  rangeList.length &&
                  rangeList[0].min > budget?.max
                ? 0
                : 49,
        ];
    }, [budget?.max, budget?.min, priceRange.rangeList]);

    const onReset = useCallback(() => {
        onClear();
        setBudget(undefined);
    }, [setBudget, onClear]);
    const onDelete = useCallback(() => {
        onReset();
    }, [onReset]);

    const headline = t('TRANS-travel-price-total');
    const disabled = !budgetMeta?.rangeList.length;

    return (
        <Segment
            withDeleteLink
            onDelete={onDelete}
            headline={{
                text: headline,
                tooltip: t('TRANS-travel-price-explanation'),
            }}
            kiFilterGroupData={{
                refKey: 'PriceRange',
                label: headline,
                multi: false,
            }}
            qaIdentifier={QaIdentifier.Search_Filter_Section_PriceSlider}
            filterList={[
                {
                    isSelected:
                        !!budget?.min && priceRange.minPrice !== budget.min,
                    filterValue: headline,
                    content: (
                        <PriceSliderComponent
                            minDistance={0}
                            key={FilterKeys.PriceRangeSlider}
                            onChange={(value) => {
                                const newValue = {
                                    min:
                                        priceRange.minPrice !== value[0]
                                            ? value[0]
                                            : null,
                                    max:
                                        priceRange.maxPrice !== value[1]
                                            ? value[1]
                                            : null,
                                };
                                onSelect(getRangeValue(newValue));
                                setBudget(newValue);
                            }}
                            onReset={onReset}
                            priceRange={priceRange}
                            initialMinPrice={budget?.min ?? priceRange.minPrice}
                            initialMaxPrice={budget?.max ?? priceRange.maxPrice}
                            initialIndexTuple={initialIndexTuple}
                            disabled={disabled}
                            customClass={{
                                Diagram: classNames({
                                    [styles.Hide]:
                                        !budgetMeta?.rangeList.length,
                                }),
                            }}
                        />
                    ),
                    kiFilterData: {
                        label: headline,
                        isSelectable: !disabled,
                        urlKey: 'budgetMin',
                        value: budget?.min?.toString() ?? '',
                        ...budgetMeta,
                    },
                },
                {
                    isSelected:
                        !!budget?.max && priceRange.maxPrice !== budget.max,
                    filterValue: headline,
                    content: null,
                    kiFilterData: {
                        label: headline,
                        isSelectable: !disabled,
                        urlKey: 'budgetMax',
                        value: budget?.max?.toString() ?? '',
                        ...budgetMeta,
                    },
                },
            ]}
        />
    );
};
export default PriceSlider;
